/**
 * What a delivery blocker means, and therefore whether it is a problem.
 *
 * The diagnostic agent writes one row per asset group or ad group — a census,
 * not a fault list. `WPP_NRI_USA_Pmax` produced 23 rows for its 23 asset
 * groups, most of them fine. The Diagnostics tab counted every row whose
 * `blockers` list was non-empty and called the total "blocked", which on one
 * account read as **520 blocked entities across 31 campaigns**.
 *
 * Across the 12 downloaded runs, 115 of 159 rows carried a blocker. By string:
 *
 *     ASSET_GROUP_PAUSED     53     you switched it off
 *     WEAK_AD_STRENGTH       39     it is serving, just thinly
 *     ASSET_GROUP_REMOVED    24     you deleted it
 *     CAMPAIGN_PAUSED         9     the whole campaign is off
 *     NO_AUDIENCE_SIGNAL      9     real — but all 9 are on paused groups
 *     COMPLIANCE_VIOLATION    3     real
 *     ADS_DISAPPROVED         3     real
 *     AD_GROUP_PAUSED         3     you switched it off
 *     ASSET_GROUP_LIMITED     1     real
 *     LIMITED_BY_POLICY       1     a policy call, outside this tool
 *     AD_GROUP_REMOVED        1     you deleted it
 *     GEO_LANGUAGE_MISMATCH   1     real
 *
 * Most of it is decisions somebody made on purpose. A removed asset group
 * cannot be unblocked — it no longer exists. Reporting either as breakage
 * buries the handful that are real, and a number nobody can trust is worse
 * than no number.
 *
 * So a blocker is classified by WHO HAS TO ACT, the only distinction the
 * reader cares about:
 *
 *   OFF      someone turned it off, deleted it, or has not started it. Not a
 *            fault. Counted quietly, never listed.
 *   BLOCKED  it should be serving and is not, and somebody here can fix it.
 *   WAITING  real, and nobody can act today — Google is reviewing it, a policy
 *            limits it, bidding is still learning. Shown as state, with no
 *            Dismiss: there is nothing to dismiss, and it returns next run.
 *   THIN     not a delivery fault at all. The entity serves. Excluded.
 *
 * The vocabulary is the closed set the five diagnostic briefs declare
 * (`{pmax,sem,fb,linkedin}_diagnostic_optimizer_launcher.py`). Anything
 * outside it is treated as BLOCKED — an unrecognised reason is more likely a
 * real fault than a state, and the safe failure is to show it. That default
 * earns its keep: NO_AUDIENCE_SIGNAL, COMPLIANCE_VIOLATION and
 * GEO_LANGUAGE_MISMATCH all reach the reader through it.
 */

/**
 * Not running, on purpose. Paused, removed, drafted, or not yet started.
 *
 * Includes the not-yet states. "The flight has not started" is not breakage
 * and there is nothing to fix; it is the same kind of fact as "it is paused".
 */
const OFF = new Set([
  'CAMPAIGN_PAUSED', 'CAMPAIGN_ENDED', 'CAMPAIGN_DRAFT',
  'ASSET_GROUP_PAUSED', 'ASSET_GROUP_REMOVED',
  'AD_GROUP_PAUSED', 'AD_GROUP_REMOVED', 'GROUP_PAUSED',
  'ALL_ADS_PAUSED', 'CREATIVES_PAUSED', 'CREATIVES_IN_DRAFT',
  'FLIGHT_ENDED', 'FLIGHT_NOT_STARTED',
  'SCHEDULE_ENDED', 'SCHEDULE_NOT_STARTED',
  'NOT_ACTIVE', 'ARCHIVED', 'PAUSED', 'REMOVED',
]);

/**
 * Real, and nobody in this tool can act on it today — you wait for Google.
 *
 * Deliberately small. A first draft also put ADS_DISAPPROVED and
 * KEYWORDS_DISAPPROVED here, which is wrong: a disapproval is a thing to FIX
 * (replace the ad, change the keyword), not a thing to wait out. Only review
 * that has not finished, a policy limit that needs a decision outside this
 * tool, and a bid strategy still learning have no action attached.
 */
const WAITING = new Set([
  'PENDING_REVIEW', 'ADS_PENDING_REVIEW', 'ADS_IN_REVIEW',
  'ASSET_GROUP_PENDING_REVIEW',
  'LIMITED_BY_POLICY', 'LEARNING', 'LEARNING_LIMITED',
]);

/**
 * Serving, just not strongly. `WEAK_AD_STRENGTH` was 39 of the 115 and is the
 * single biggest inflator after the off-states. It already produces its own
 * suggestion cards, so counting it here says the same thing twice — once as a
 * recommendation and once as a failure.
 */
const THIN = new Set(['WEAK_AD_STRENGTH', 'THIN_KEYWORD_COVERAGE']);

export const BLOCKER_CLASS = { OFF: 'off', BLOCKED: 'blocked', WAITING: 'waiting', THIN: 'thin' };

/** The class of one blocker string. Unknown reasons count as BLOCKED. */
export function classifyBlocker(blocker) {
  const key = String(blocker || '').trim().toUpperCase();
  if (!key || key === 'NONE') return null;
  if (OFF.has(key)) return BLOCKER_CLASS.OFF;
  if (WAITING.has(key)) return BLOCKER_CLASS.WAITING;
  if (THIN.has(key)) return BLOCKER_CLASS.THIN;
  return BLOCKER_CLASS.BLOCKED;
}

const listOf = (entity) => (Array.isArray(entity?.blockers) ? entity.blockers : []);

/**
 * The class of an entity, from the blocker that explains the most.
 *
 * OFF WINS OVER EVERYTHING, and that is the whole point. A third of the rows
 * carry more than one blocker, and the agent keeps diagnosing an entity after
 * noting it is switched off: nine rows read
 * `ASSET_GROUP_PAUSED + CAMPAIGN_PAUSED + NO_AUDIENCE_SIGNAL +
 * WEAK_AD_STRENGTH`, and two read `ADS_DISAPPROVED + AD_GROUP_PAUSED`. Ranking
 * the fixable reason first puts all eleven back in the blocked count and asks
 * the reader to add an audience signal to an asset group inside a paused
 * campaign. "It is off" is the complete explanation for why it is not
 * delivering, and nothing else about it matters until someone turns it on.
 *
 * Below that: BLOCKED > WAITING > THIN, most actionable first.
 *
 * `can_deliver` and `severity` are used ONLY as a fallback, when no recognised
 * blocker is present. They cannot be trusted alongside one: on the 30 Aug runs
 * every paused and removed asset group was written as
 * `can_deliver: false, severity: HARD_BLOCKER` — deleted inventory reported as
 * a hard failure — and identical WEAK_AD_STRENGTH rows split 9 `can_deliver:
 * true` against 5 `false` on groups that were all serving. The blocker string
 * is the reliable field; those two are the model's colouring of it.
 */
export function classifyEntity(entity) {
  if (!entity) return null;
  const classes = new Set(listOf(entity).map(classifyBlocker).filter(Boolean));
  if (classes.has(BLOCKER_CLASS.OFF)) return BLOCKER_CLASS.OFF;
  if (classes.has(BLOCKER_CLASS.BLOCKED)) return BLOCKER_CLASS.BLOCKED;
  if (classes.has(BLOCKER_CLASS.WAITING)) return BLOCKER_CLASS.WAITING;
  if (classes.has(BLOCKER_CLASS.THIN)) return BLOCKER_CLASS.THIN;
  const severity = String(entity.severity || '').toUpperCase();
  if (entity.can_deliver === false || severity === 'HARD_BLOCKER') {
    return BLOCKER_CLASS.BLOCKED;
  }
  return null;
}

/** True when this entity should be listed and counted as blocked. */
export const entityBlocked = (e) => classifyEntity(e) === BLOCKER_CLASS.BLOCKED;

/** True when this entity is real but nobody can act on it today. */
export const entityWaiting = (e) => classifyEntity(e) === BLOCKER_CLASS.WAITING;

/** True when this entity is off on purpose — counted quietly, never listed. */
export const entityOff = (e) => classifyEntity(e) === BLOCKER_CLASS.OFF;

/**
 * Counts for one campaign's diagnosis, for the summary chips.
 *
 * `thin` is deliberately not returned: it is not a delivery state and it is
 * already represented by the suggestion cards.
 */
export function countEntities(entities) {
  const rows = Array.isArray(entities) ? entities : [];
  let blocked = 0;
  let waiting = 0;
  let off = 0;
  rows.forEach((e) => {
    const cls = classifyEntity(e);
    if (cls === BLOCKER_CLASS.BLOCKED) blocked += 1;
    else if (cls === BLOCKER_CLASS.WAITING) waiting += 1;
    else if (cls === BLOCKER_CLASS.OFF) off += 1;
  });
  return { blocked, waiting, off, total: rows.length };
}



/**
 * The entities a card demonstrably names, keyed for matching against diagnosis
 * rows: `id::<target_id>` and `name::<target_name>`.
 *
 * DELIBERATELY CONSERVATIVE, because a card's target is not always an entity.
 * Across the live runs it takes three shapes:
 *
 *   target_id 6719293029, target_name "UPI (2)"        one asset group
 *   target_id "multiple",  target_name "23 Disapproved Asset Groups"
 *   target_id null,        target_name "10 Asset Groups"
 *   target_id 23877581268, target_type "campaign"      the whole campaign
 *
 * Only the first is a claim about a specific entity. An aggregate card names a
 * COUNT, not a list, and nothing in the payload says which 23 asset groups it
 * meant — so folding rows into it would be guessing which ones are covered. A
 * campaign-scope card is excluded for the same reason: "fix this campaign's
 * negative keywords" does not account for a particular ad group's blocker.
 */
const AGGREGATE_IDS = new Set(['multiple', 'various', 'all', 'none']);

export function cardEntityKeys(cards) {
  const keys = new Set();
  (Array.isArray(cards) ? cards : []).forEach((c) => {
    if (!c || String(c.tt || '').toLowerCase() === 'campaign') return;
    // The explicit list an aggregate card carries. This is the only thing that
    // makes a "10 Asset Groups" card foldable, and until runs emit it the
    // matcher below can only reach single-entity cards.
    (Array.isArray(c.tids) ? c.tids : []).forEach((t) => {
      const v = String(t ?? '').trim();
      if (v && !AGGREGATE_IDS.has(v.toLowerCase())) keys.add(`id::${v.toLowerCase()}`);
    });
    const id = String(c.tid ?? '').trim();
    if (id && !AGGREGATE_IDS.has(id.toLowerCase())) keys.add(`id::${id.toLowerCase()}`);
    const name = String(c.tn ?? '').trim();
    // A name that opens with a count is a label for a group, not an entity:
    // "10 Asset Groups", "23 Disapproved Asset Groups", "3 Enabled Asset Groups".
    if (name && !/^\d+\s/.test(name)) keys.add(`name::${name.toLowerCase()}`);
  });
  return keys;
}

/** The keys one diagnosis row could be matched by. */
export function entityKeys(entity) {
  const keys = [];
  const id = entity?.entity_id ?? entity?.asset_group_id ?? entity?.ad_group_id;
  const name = entity?.entity_name ?? entity?.asset_group_name ?? entity?.ad_group_name;
  if (id !== undefined && id !== null && String(id).trim()) {
    keys.push(`id::${String(id).trim().toLowerCase()}`);
  }
  if (name && String(name).trim()) keys.push(`name::${String(name).trim().toLowerCase()}`);
  return keys;
}

/**
 * True when a card already covers this entity, so the diagnosis row would be
 * the same finding told twice.
 *
 * The cards are the actionable form and they carry the fix, the spend at risk
 * and the apply button; the diagnosis row carries the same claim with none of
 * that. When both exist the card is strictly better, and two of them is how a
 * section of 31 rows describes five findings.
 */
export function coveredByCard(entity, cardKeys) {
  if (!cardKeys || !cardKeys.size) return false;
  // ONLY a row that would otherwise be LISTED can be folded. A switched-off or
  // thin row is never listed, so folding it changes nothing a reader sees — but
  // it does remove the row from the header's counts, and a card that overreaches
  // then silently deletes the "switched off" total. AUS_Pmax's first
  // post-deploy run did exactly that: one card named all 49 asset groups, 38 of
  // them paused, and the header went from "38 switched off" to nothing.
  //
  // The card being wrong is fixed in the brief. This makes the page's numbers
  // independent of whether that fix holds.
  const cls = classifyEntity(entity);
  if (cls !== BLOCKER_CLASS.BLOCKED && cls !== BLOCKER_CLASS.WAITING) return false;
  return entityKeys(entity).some((k) => cardKeys.has(k));
}

export default {
  BLOCKER_CLASS, classifyBlocker, classifyEntity, countEntities,
  entityBlocked, entityWaiting, entityOff,
  cardEntityKeys, entityKeys, coveredByCard,
};
